// src/Pages/BDPOReport.jsx
import { useState, useEffect } from "react";
import { Button, Table, Spin, Alert, message, Tag } from "antd";
import { FileExcelOutlined, ReloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import "jspdf-autotable";
import axios from "axios";

const BDPOReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const columns = [
    {
      title: "Sr.",
      dataIndex: "sr_no",
      key: "sr_no",
      width: 45,
      fixed: "left",
      align: "center",
    },
    {
      title: "District",
      dataIndex: "district_name",
      key: "district_name",
      width: 100,
      align: "center",
      sorter: (a, b) => a.district_name?.localeCompare(b.district_name),
      ellipsis: true,
    },
    {
      title: "Block Count",
      dataIndex: "block_count",
      key: "block_count",
      width: 70,
      align: "center",
    },
    {
      title: "GP Count",
      dataIndex: "gp_count",
      key: "gp_count",
      width: 70,
      align: "center",
    },
    {
      title: "Special GS Conducted",
      dataIndex: "total_special_gram_sabha",
      key: "total_special_gram_sabha",
      width: 100,
      align: "center",
    },
    {
      title: "Total Beneficiaries",
      dataIndex: "total_beneficiaries",
      key: "total_beneficiaries",
      width: 90,
      align: "center",
    },
    {
      title: "Eligible Beneficiaries",
      dataIndex: "total_eligible_beneficiaries",
      key: "total_eligible_beneficiaries",
      width: 100,
      align: "center",
      render: (val) => <Tag color="blue" style={{ fontSize: "10px" }}>{val || 0}</Tag>,
    },
    {
      title: "Income Verified (Sachiv)",
      dataIndex: "total_income_verified_by_sachiv",
      key: "total_income_verified_by_sachiv",
      width: 110,
      align: "center",
      render: (val) => <Tag color="green" style={{ fontSize: "10px" }}>{val || 0}</Tag>,
    },
    {
      title: "Families Intentionally Split",
      dataIndex: "total_families_intentionally_spitted",
      key: "total_families_intentionally_spitted",
      width: 110,
      align: "center",
      render: (val) => <Tag color="red" style={{ fontSize: "10px" }}>{val || 0}</Tag>,
    },
  ];

  const apiEndpoint = "/api/reports/bdpo-summary";

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem("token");

      if (!token) {
        setError("No authentication token found. Please login again.");
        setLoading(false);
        return;
      }

      const res = await axios.post(apiEndpoint, { role: "bdpo" }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const reportData = res?.data?.result || [];
      const formattedData = Array.isArray(reportData)
        ? reportData.map((item, index) => ({
            ...item,
            key: item.district_code || `row-${index}`,
            sr_no: index + 1,
          }))
        : [];

      setData(formattedData);
      if (formattedData.length > 0) {
        message.success(`Loaded ${formattedData.length} records`);
      }
    } catch (err) {
      console.error("Error fetching report data:", err);
      setError(err.message || "An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleExportPDF = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await axios.post(apiEndpoint, { role: "bdpo" }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const reportData = res?.data?.result || [];

      if (!reportData || reportData.length === 0) {
        message.warning("No data to export");
        return;
      }

      const doc = new jsPDF("landscape");

      doc.setFontSize(16);
      doc.text("BDPO Consolidated Report", 14, 16);

      doc.setFontSize(10);
      const now = new Date();
      doc.text(`Generated on: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`, 14, 24);

      const head = [columns.filter(c => c.dataIndex !== 'sr_no').map((c) => c.title)];
      const body = reportData.map((row, idx) =>
        columns.filter(c => c.dataIndex !== 'sr_no').map((c) => {
          const value = row[c.dataIndex];
          return value !== null && value !== undefined ? value.toString() : "";
        })
      );

      doc.autoTable({
        startY: 30,
        head,
        body,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [21, 29, 87], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 10, right: 10 },
        theme: 'grid'
      });

      const fileName = `BDPO_Consolidated_Report_${now.toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      message.success("PDF exported successfully");
    } catch (err) {
      console.error("Export PDF error:", err);
      message.error("Failed to export PDF");
    }
  };

  const handleExportExcel = () => {
    if (!data || data.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const csvContent = [
        columns.map(col => `"${col.title}"`).join(','),
        ...data.map(row =>
          columns.map(col => {
            const value = row[col.dataIndex];
            return `"${value !== null && value !== undefined ? value.toString().replace(/"/g, '""') : ''}"`;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `BDPO_Report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success("Excel exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel");
    }
  };

  return (
    <div className="report-container">
      <div className="report-card">
        <div className="report-header">
          <div>
            <h2 className="report-title">BDPO Consolidated Report</h2>
            <p className="report-subtitle">
              Total Records: <strong>{data.length}</strong>
            </p>
          </div>
          <div className="report-actions">
            <Button
              onClick={handleExportPDF}
              disabled={loading || data.length === 0}
              icon={<FilePdfOutlined />}
            >
              Export PDF
            </Button>
            <Button
              type="primary"
              onClick={handleExportExcel}
              disabled={loading || data.length === 0}
              icon={<FileExcelOutlined />}
              className="btn-export"
            >
              Export Excel
            </Button>
            <Button
              onClick={fetchReportData}
              disabled={loading}
              icon={<ReloadOutlined />}
              className="btn-refresh"
            >
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setError(null)}
          />
        )}

        {loading ? (
          <div className="loading-container">
            <Spin size="large" tip="Loading report data..." />
          </div>
        ) : data.length > 0 ? (
          <Table
            columns={columns}
            dataSource={data}
            rowKey="key"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              pageSizeOptions: ['10', '25', '50', '100'],
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
              total: data.length,
            }}
            onChange={handleTableChange}
            scroll={{ x: 900 }}
            bordered
            size="small"
          />
        ) : (
          <Alert
            message="No Data Available"
            description="No report data found. Click the Refresh button to try again."
            type="info"
            showIcon
            action={
              <Button size="small" type="primary" onClick={fetchReportData}>
                Refresh Data
              </Button>
            }
          />
        )}
      </div>
    </div>
  );
};

export default BDPOReport;
